import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Search, Plus, Pill, X } from "lucide-react";

export default function DrugsPage() {
  const [drugs, setDrugs] = useState([]);
  const [q, setQ] = useState("");
  const [showReq, setShowReq] = useState(false);
  const [form, setForm] = useState({ name: "", generic_name: "", brand_names: "", description: "", default_route: "Oral", default_location: "Facility Administered", color: "#0EA5E9" });
  const [msg, setMsg] = useState("");

  const load = () => api.get(`/drugs${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setDrugs(r.data));
  useEffect(() => { load(); }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/drug-requests", { ...form, brand_names: form.brand_names.split(",").map((s) => s.trim()).filter(Boolean) });
      setShowReq(false);
      setForm({ name: "", generic_name: "", brand_names: "", description: "", default_route: "Oral", default_location: "Facility Administered", color: "#0EA5E9" });
      setMsg("Request submitted — pending admin approval.");
      setTimeout(() => setMsg(""), 3500);
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="drugs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">System Drug Library</h1>
          <p className="text-stone-500 mt-1">{drugs.length} drugs available. Request a new drug if it's not listed.</p>
        </div>
        <button onClick={() => setShowReq(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold" data-testid="request-drug-btn">
          <Plus className="w-4 h-4" />Request new drug
        </button>
      </div>

      {msg && <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm" data-testid="drug-request-success">{msg}</div>}

      <div className="bg-white border border-stone-200 rounded-xl shadow-soft p-4 mb-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input data-testid="drug-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search drugs by name or brand…"
            className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl shadow-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-wide text-stone-500">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Drug</th>
              <th className="text-left px-5 py-3 font-medium">Brand(s)</th>
              <th className="text-left px-5 py-3 font-medium">Route</th>
              <th className="text-left px-5 py-3 font-medium">Location</th>
              <th className="text-left px-5 py-3 font-medium">Tag</th>
            </tr>
          </thead>
          <tbody>
            {drugs.map((d) => (
              <tr key={d.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`drug-row-${d.id}`}>
                <td className="px-5 py-3 font-semibold text-stone-800 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: d.color || "#A8A29E" }} />
                  {d.name}
                </td>
                <td className="px-5 py-3 text-stone-600">{(d.brand_names || []).join(", ") || "—"}</td>
                <td className="px-5 py-3 text-stone-600">{d.default_route}</td>
                <td className="px-5 py-3 text-stone-600">{d.default_location}</td>
                <td className="px-5 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{d.default_route?.toLowerCase().includes("oral") ? "PO" : d.default_route?.toLowerCase().includes("sub") ? "SC" : "IV"}</span></td>
              </tr>
            ))}
            {drugs.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-stone-500">No drugs match your search.</td></tr>}
          </tbody>
        </table>
      </div>

      {showReq && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="drug-request-modal">
          <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md border border-stone-200">
            <div className="px-5 py-4 border-b border-stone-100 flex justify-between items-center">
              <h3 className="font-display font-semibold text-stone-900">Request new system drug</h3>
              <button onClick={() => setShowReq(false)}><X className="w-5 h-5 text-stone-400" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Drug name*</label>
                <input data-testid="drug-req-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Generic name</label>
                <input value={form.generic_name} onChange={(e) => setForm({ ...form, generic_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Brand names (comma separated)</label>
                <input value={form.brand_names} onChange={(e) => setForm({ ...form, brand_names: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Route*</label>
                  <select value={form.default_route} onChange={(e) => setForm({ ...form, default_route: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none">
                    {["Oral","Intravenous","Subcutaneous","Intramuscular","Topical"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Location*</label>
                  <select value={form.default_location} onChange={(e) => setForm({ ...form, default_location: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none">
                    {["Facility Administered","Home Administration","Inpatient Admission","Outpatient"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Description / Clinical use</label>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Calendar color</label>
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-16 h-10 rounded-lg border border-stone-300" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowReq(false)} className="px-3 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50">Cancel</button>
                <button data-testid="drug-req-submit" type="submit" className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white text-sm font-semibold">Submit request</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
